import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Billing from "@/pages/Billing";
import PaymentResult from "@/pages/PaymentResult";
import Dashboard from "@/pages/Dashboard";
import Placeholder from "@/pages/Placeholder";
import Warehouses from "@/pages/Warehouses";
import Inventory from "@/pages/Inventory";
import InventoryHub from "@/pages/InventoryHub";
import MasterData from "@/pages/MasterData";
import Procurement from "@/pages/Procurement";
import Production from "@/pages/Production";
import Users from "@/pages/Users";
import Suppliers from "@/pages/Suppliers";
import Logistics from "@/pages/Logistics";
import FinishedGoods from "@/pages/FinishedGoods";
import ProductionRequests from "@/pages/ProductionRequests";
import Finance from "@/pages/Finance";
import GoodsReceipt from "@/pages/GoodsReceipt";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import NotAuthorized from "@/pages/NotAuthorized";
import { useAuth } from "@/context/AuthContext";

function Guard({ permission, children }) {
  const { hasPerm } = useAuth();
  if (permission && !hasPerm(permission)) return <NotAuthorized />;
  return children;
}

function GuardAny({ permissions, children }) {
  const { hasPerm } = useAuth();
  if (permissions && !permissions.some((p) => hasPerm(p))) return <NotAuthorized />;
  return children;
}

function Protected({ children }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
            <Route path="/payment/cancel" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/warehouses" element={<Protected><Guard permission="warehouse:view"><Warehouses /></Guard></Protected>} />
            <Route path="/master-data" element={<Protected><Guard permission="masterdata:view"><MasterData /></Guard></Protected>} />
            <Route path="/inventory" element={<Protected><Guard permission="inventory:view"><InventoryHub /></Guard></Protected>} />
            <Route path="/goods-receipt" element={<Protected><Guard permission="procurement:view"><GoodsReceipt /></Guard></Protected>} />
            <Route path="/procurement" element={<Protected><Guard permission="procurement:view"><Procurement /></Guard></Protected>} />
            <Route path="/suppliers" element={<Protected><Guard permission="procurement:view"><Suppliers /></Guard></Protected>} />
            <Route path="/logistics" element={<Protected><Guard permission="logistics:view"><Logistics /></Guard></Protected>} />
            <Route path="/finished-goods" element={<Protected><Guard permission="dashboard:view"><FinishedGoods /></Guard></Protected>} />
            <Route path="/production-requests" element={<Protected><GuardAny permissions={["warehouse:view", "production:view"]}><ProductionRequests /></GuardAny></Protected>} />
            <Route path="/finance" element={<Protected><Guard permission="finance:view"><Finance /></Guard></Protected>} />
            <Route path="/billing" element={<Protected><Guard permission="users:view"><Billing /></Guard></Protected>} />
            <Route path="/production" element={<Protected><Guard permission="production:view"><Production /></Guard></Protected>} />
            <Route path="/admin/users" element={<Protected><Guard permission="users:view"><Users /></Guard></Protected>} />
            <Route path="/system" element={<Protected><Placeholder title="System Health" /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
